/**
 * Message translations under the bubbles (CRM chats.js translateMessageRow and
 * chat-transcript.js auto-translate): MyMemory from the person's browser, into
 * their message language. One asked by hand stays when auto-translation is
 * turned off; at most two run at once.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { autoTranslatePaused, messageTranslateTarget, pauseAutoTranslateForToday } from './composerPrefs';
import type { ChatMessage } from './messages';
import { detectLanguage, TranslateQuotaError, translateText } from './translate';

export type Translation = { state: 'busy' | 'done' | 'error'; text: string; manual: boolean };

const AT_ONCE = 2;

export function useTranslations(chatKey: string) {
  const [map, setMap] = useState<Map<string, Translation>>(new Map());
  const queue = useRef<{ id: string; text: string; manual: boolean; onError?: (err: unknown) => void }[]>([]);
  const running = useRef(0);
  const key = useRef(chatKey);
  key.current = chatKey;
  /** Messages auto-translation already took (or skipped). */
  const tried = useRef(new Set<string>());

  useEffect(() => {
    queue.current = [];
    tried.current = new Set();
    setMap(new Map());
  }, [chatKey]);

  const set = (id: string, t: Translation) => setMap((prev) => new Map(prev).set(id, t));

  const drain = useCallback(() => {
    while (running.current < AT_ONCE && queue.current.length) {
      const job = queue.current.shift()!;
      const forKey = key.current;
      running.current += 1;
      translateText(job.text, messageTranslateTarget())
        .then((text) => key.current === forKey && set(job.id, { state: 'done', text, manual: job.manual }))
        .catch((err: unknown) => {
          if (key.current !== forKey) return;
          if (job.manual) set(job.id, { state: 'error', text: (err as Error).message, manual: true });
          else setMap((prev) => (prev.get(job.id)?.manual ? prev : new Map(prev).set(job.id, { state: 'error', text: '', manual: false })));
          job.onError?.(err);
        })
        .finally(() => {
          running.current -= 1;
          drain();
        });
    }
  }, []);

  /** Translate one message; `manual` = asked from its menu. */
  const translate = useCallback(
    (id: string, text: string, manual: boolean, onError?: (err: unknown) => void) => {
      set(id, { state: 'busy', text: '', manual });
      queue.current.push({ id, text, manual, onError });
      drain();
    },
    [drain],
  );

  /** Auto-translation was turned off: drop what it showed, keep what was asked for. */
  const dropAuto = useCallback(() => {
    queue.current = queue.current.filter((j) => j.manual);
    tried.current = new Set();
    setMap((prev) => new Map([...prev].filter(([, t]) => t.manual)));
  }, []);

  /**
   * A fan's message came into view with auto-translation on: translate it once
   * when it has words in another language than the person's. A spent daily
   * quota pauses auto-translation until tomorrow.
   */
  const seen = useCallback(
    (m: ChatMessage) => {
      if (m.side !== 'fan' || tried.current.has(m.id) || autoTranslatePaused()) return;
      tried.current.add(m.id);
      if (!/\p{L}{2,}/u.test(m.text) || detectLanguage(m.text) === messageTranslateTarget()) return;
      translate(m.id, m.text, false, (err) => {
        if (!(err instanceof TranslateQuotaError)) return;
        pauseAutoTranslateForToday();
        queue.current = queue.current.filter((j) => j.manual);
      });
    },
    [translate],
  );

  return { translations: map, translate, dropAuto, seen };
}
